import { useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Info } from "lucide-react";
import Papa from "papaparse";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface WearableData {
  date: string;
  steps?: number;
  distance?: number;
  calories?: number;
  activeMinutes?: number;
}

interface WearableImportDialogProps {
  onImport: (data: WearableData[]) => void;
}

const WearableImportDialog = ({ onImport }: WearableImportDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [importedData, setImportedData] = useState<WearableData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const parsed = results.data.map((row: any) => ({
            date: row.date || row.Date || row.DATE || "",
            steps: parseInt(row.steps || row.Steps || row.STEPS || "0"),
            distance: parseFloat(row.distance || row.Distance || row.DISTANCE || "0"),
            calories: parseFloat(row.calories || row.Calories || row.CALORIES || "0"),
            activeMinutes: parseInt(row.active_minutes || row.activeMinutes || row.ActiveMinutes || "0"),
          })).filter((item) => item.date); // Only keep rows with valid dates

          if (parsed.length === 0) {
            setError("No valid data found in CSV file. Please check the format.");
            setIsProcessing(false);
            return;
          }

          setImportedData(parsed);

          // Store in database
          const sessionId = `session_${Date.now()}`;
          const { error: dbError } = await supabase
            .from("wearable_imports")
            .insert({
              session_id: sessionId,
              import_data: parsed,
              source: file.name,
            });

          if (dbError) {
            console.error("Error storing import:", dbError);
          }

          toast.success(`Successfully imported ${parsed.length} records`);
          setIsProcessing(false);
        } catch (err) {
          setError("Failed to parse CSV file. Please check the format.");
          setIsProcessing(false);
          console.error(err);
        }
      },
      error: (err) => {
        setError(`Error reading file: ${err.message}`);
        setIsProcessing(false);
      },
    });
  };

  const handleApplyData = () => {
    if (importedData.length > 0) {
      onImport(importedData);
      toast.success("Wearable data applied to calculator");
      setIsOpen(false);
      setImportedData([]);
    }
  };

  const calculateWeeklySummary = () => {
    if (importedData.length === 0) return null;

    const total = importedData.reduce(
      (acc, day) => ({
        steps: acc.steps + (day.steps || 0),
        distance: acc.distance + (day.distance || 0),
        calories: acc.calories + (day.calories || 0),
        activeMinutes: acc.activeMinutes + (day.activeMinutes || 0),
      }),
      { steps: 0, distance: 0, calories: 0, activeMinutes: 0 }
    );

    return {
      ...total,
      avgSteps: Math.round(total.steps / importedData.length),
      avgDistance: (total.distance / importedData.length).toFixed(2),
      avgCalories: Math.round(total.calories / importedData.length),
    };
  };

  const summary = calculateWeeklySummary();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Upload className="h-4 w-4" />
          Import Wearable Data
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Import Wearable Activity Data
          </DialogTitle>
          <DialogDescription>
            Upload CSV data from Fitbit, Google Fit, or other fitness trackers to automatically calculate weekly activity metrics.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>CSV Format:</strong> Your file should include columns for: date, steps, distance, calories, and/or active_minutes.
              Column names are case-insensitive.
            </AlertDescription>
          </Alert>

          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload"
              disabled={isProcessing}
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm font-medium mb-1">
                {isProcessing ? "Processing..." : "Click to upload CSV file"}
              </p>
              <p className="text-xs text-muted-foreground">
                Supports Fitbit, Google Fit, and similar formats
              </p>
            </label>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {importedData.length > 0 && summary && (
            <Card className="p-4 bg-accent/5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <h4 className="font-semibold">Import Summary</h4>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Total Days</p>
                  <p className="text-lg font-bold">{importedData.length}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Avg Steps/Day</p>
                  <p className="text-lg font-bold">{summary.avgSteps.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Avg Distance</p>
                  <p className="text-lg font-bold">{summary.avgDistance} km</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Calories</p>
                  <p className="text-lg font-bold">{summary.calories.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Avg Calories/Day</p>
                  <p className="text-lg font-bold">{summary.avgCalories}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Active Min</p>
                  <p className="text-lg font-bold">{summary.activeMinutes}</p>
                </div>
              </div>
              <Button 
                onClick={handleApplyData} 
                className="w-full mt-4 gap-2"
              >
                <CheckCircle2 className="h-4 w-4" />
                Apply to Calculator
              </Button>
            </Card>
          )}

          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <h4 className="text-sm font-semibold">Sample CSV Format:</h4>
            <pre className="text-xs overflow-x-auto bg-background p-3 rounded">
{`date,steps,distance,calories,active_minutes
2025-01-01,8500,6.2,320,45
2025-01-02,10200,7.5,385,52
2025-01-03,7800,5.8,295,38`}
            </pre>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WearableImportDialog;